import React from 'react';
import { checkAuthError } from '../utils/sessionUtils';

// Las preferencias son de la cuenta, pero el permiso de notificaciones es de cada navegador.
// 'granted' | 'denied' | 'default' (aun no se ha preguntado) | 'unsupported'
export const pushState = () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
};

const saveSettings = async (body) => {
    const res = await fetch('/api/settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });
    const data = await res.json();
    if (checkAuthError(data)) return data;
    if (!data.success) throw new Error(data.error);
    return data;
};

// El service worker de las notificaciones se registra en un ambito donde no vive ninguna pagina:
// para recibir avisos no necesita controlar ninguna, y asi ninguna pestaña lo ve aparecer en
// navigator.serviceWorker.ready. Registrado en la raiz, las pestañas abiertas desde antes de
// que existiera (el index.js de entonces dejaba pendiente un ready.then(unregister)) lo
// desregistraban nada mas activarse, en mitad de la suscripcion.
const SW_SCOPE = '/sw-push/';

// subscribe() exige que el registro tenga un service worker activo. No vale esperar a
// navigator.serviceWorker.ready: esta pagina queda fuera del ambito y no se resolveria nunca.
const whenActive = (registration) => new Promise((resolve, reject) => {
    if (registration.active) return resolve();
    const worker = registration.installing || registration.waiting;
    worker.addEventListener('statechange', () => {
        if (registration.active) resolve();
        else if (worker.state === 'redundant') reject(new Error('No se ha podido instalar el service worker'));
    });
});

// Pide el permiso si hace falta, suscribe este navegador y manda la suscripcion al servidor.
// Devuelve el estado en el que queda el navegador ('error' si el navegador no pudo suscribirse).
async function subscribePush(pushKey) {
    if (pushState() === 'unsupported') return 'unsupported';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission;
    try {
        // Quien se suscribio el primer dia lo hizo en la raiz: se quita ese registro (y con el
        // su suscripcion) para que no le lleguen los avisos por duplicado.
        const legacy = await navigator.serviceWorker.getRegistration('/');
        if (legacy) await legacy.unregister();
        const registration = await navigator.serviceWorker.register('/sw.js', { scope: SW_SCOPE });
        await whenActive(registration);
        const options = { userVisibleOnly: true, applicationServerKey: pushKey };
        let subscription;
        try {
            subscription = await registration.pushManager.subscribe(options);
        } catch (err) {
            // Queda una suscripcion hecha con otra clave del servidor: se cambia por una nueva.
            const old = await registration.pushManager.getSubscription();
            if (!old) throw err;
            await old.unsubscribe();
            subscription = await registration.pushManager.subscribe(options);
        }
        await saveSettings({ subscription: subscription });
        return 'granted';
    } catch (err) {
        console.error('Error activando las notificaciones:', err);
        return 'error';
    }
}

const Toggle = ({ label, hint, checked, disabled, onChange }) => (
    <div className='settingRow'>
        <div>
            <div>{label}</div>
            {hint ? <div className='settingHint'>{hint}</div> : null}
        </div>
        <button type='button' role='switch' aria-checked={checked} aria-label={label} disabled={disabled}
            className={'toggleForallia heroes' + (checked ? ' on' : '')} onClick={() => onChange(!checked)}>
            {checked ? 'ON' : 'OFF'}
        </button>
    </div>
);

const PUSH_HINTS = {
    granted: 'Activadas en este dispositivo.',
    denied: 'Tienes bloqueadas las notificaciones de forallia.com en este navegador: permítelas desde el candado de la barra de direcciones.',
    unsupported: 'Este navegador no admite notificaciones. Puedes recibir los avisos por mail.',
    error: 'No se han podido activar en este navegador. Puedes recibir los avisos por mail.'
};

const Notificaciones = () => {
    const [settings, setSettings] = React.useState(null);
    const [push, setPush] = React.useState(pushState());
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        fetch('/api/settings', { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (checkAuthError(data)) return;
                if (!data.success) {
                    setError('No se han podido cargar tus ajustes.');
                    return;
                }
                setSettings(data);
                // Con el permiso ya dado se renueva la suscripcion de este navegador sin preguntar.
                const { codes, live, arena } = data.notifications;
                if ((codes || live || arena) && pushState() === 'granted') {
                    subscribePush(data.pushKey).then(setPush);
                }
            })
            .catch(() => setError('No se han podido cargar tus ajustes.'));
    }, []);

    const toggle = async (key, value) => {
        setError('');
        try {
            // Al encender un aviso se pide el permiso del navegador. La preferencia se guarda aunque
            // lo deniegue: puede seguir llegándole por mail o en otro dispositivo.
            if (value && key !== 'mail') setPush(await subscribePush(settings.pushKey));
            await saveSettings({ [key]: value });
            setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, [key]: value } }));
        } catch (err) {
            setError('No se ha podido guardar el cambio. Inténtalo de nuevo.');
        }
    };

    if (!settings) {
        return error ? <p role='alert'>{error}</p> : <div className='preSpinner'><div className='spinner'></div></div>;
    }
    const { codes, live, arena, mail } = settings.notifications;
    return (
        <div className='settingsList'>
            <Toggle label='Códigos nuevos' hint='Te avisamos cuando se publica un código o premio nuevo en la web.'
                checked={codes} onChange={(value) => toggle('codes', value)} />
            <Toggle label='Directos' hint='Te avisamos cuando NaruxForAll empieza un directo.'
                checked={live} onChange={(value) => toggle('live', value)} />
            <Toggle label='Arena' hint='Si te quedas sin combates, te avisamos en cuanto recuperas uno y puedes volver a luchar.'
                checked={arena} onChange={(value) => toggle('arena', value)} />
            <Toggle label='También por mail'
                hint={settings.email
                    ? 'Los avisos que tengas encendidos llegan también a ' + settings.email + '.'
                    : 'Aún no tenemos tu correo: se guarda la próxima vez que inicies sesión.'}
                checked={mail} disabled={!settings.email && !mail} onChange={(value) => toggle('mail', value)} />
            {codes || live || arena ? (
                push === 'default' ? (
                    <p className='settingHint'>
                        En este dispositivo aún no están activadas.{' '}
                        <button type='button' className='miniBtn' onClick={async () => setPush(await subscribePush(settings.pushKey))}>Activar aquí</button>
                    </p>
                ) : <p className='settingHint'>{PUSH_HINTS[push]}</p>
            ) : null}
            {error ? <p role='alert'>{error}</p> : null}
        </div>
    );
};

const NAME_ERRORS = {
    taken: 'Ya hay alguien con ese nombre en este juego, elige otro.',
    banned: 'Ese nombre no se puede usar, prueba con otro.',
    empty: 'Escribe un nombre.',
    invalid_game: 'Ese juego ya no está activo.'
};

// El nombre de jugador en un juego: con el que se entra en la cola del directo (!cola).
const NombreJuego = ({ game }) => {
    const [name, setName] = React.useState(game.name);
    const [saved, setSaved] = React.useState(game.name);
    const [msg, setMsg] = React.useState('');

    const save = async (e) => {
        e.preventDefault();
        setMsg('');
        try {
            const res = await fetch('/api/nombres', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gameId: game.gameId, name })
            });
            const data = await res.json();
            if (checkAuthError(data)) return;
            if (!data.success) return setMsg(NAME_ERRORS[data.error] || 'No se ha podido guardar. Inténtalo de nuevo.');
            setMsg('Guardado.');
            setName(data.name);
            setSaved(data.name);
        } catch (err) {
            setMsg('No se ha podido guardar. Inténtalo de nuevo.');
        }
    };

    return (
        <form className='settingRow nombreRow' onSubmit={save}>
            <div className='settingName'>{game.game}</div>
            <input className='userSearch' value={name} maxLength={30} placeholder='Tu nombre'
                aria-label={'Tu nombre en ' + game.game} onChange={(e) => setName(e.target.value)} />
            <button type='submit' className='miniBtn' disabled={!name.trim() || name === saved}>Guardar</button>
            {msg ? <div className='settingHint' role='status'>{msg}</div> : null}
        </form>
    );
};

const Nombres = () => {
    const [games, setGames] = React.useState(null);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        fetch('/api/nombres', { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (checkAuthError(data)) return;
                if (!data.success) return setError('No se han podido cargar tus nombres.');
                setGames(data.games);
            })
            .catch(() => setError('No se han podido cargar tus nombres.'));
    }, []);

    if (!games) {
        return error ? <p role='alert'>{error}</p> : <div className='preSpinner'><div className='spinner'></div></div>;
    }
    return (
        <div className='settingsList'>
            <p className='settingHint'>
                Tu nombre de jugador en cada juego: es el que sale en la cola del directo cuando escribes !cola en el chat.
                En cada juego no puede haber dos personas con el mismo nombre.
            </p>
            {games.map(game => <NombreJuego key={game.gameId} game={game} />)}
        </div>
    );
};

// Cada apartado de Ajustes es una pestaña: [clave, titulo, componente].
const TABS = [
    ['notificaciones', 'Notificaciones', Notificaciones],
    ['nombres', 'Nombres', Nombres]
];

const Settings = () => {
    const [tab, setTab] = React.useState(TABS[0][0]);
    const Tab = TABS.find(([key]) => key === tab)[2];
    return (
        <div className='page'>
            <div className='topTickets settingsPage heroes'>
                <h1>Ajustes</h1>
                <div className='navTops unselectable'>
                    {TABS.map(([key, title]) => (
                        <div key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{title}</div>
                    ))}
                </div>
                <Tab />
            </div>
        </div>
    );
};

export default Settings;
